/**
 * SessionControlButtons Component
 * 
 * Phone-style circular control buttons for session management
 * - Pause/Resume button (yellow/blue circle with pause/play icon)
 * - End Call button (red circle with phone hangup icon)
 * - Desktop: Centered between message bubbles
 * - Mobile: Bottom of screen
 */

import { useTranslation } from 'react-i18next';
import { PauseIcon, PlayIcon, PhoneIcon } from '@heroicons/react/24/solid';

interface SessionControlButtonsProps {
  onPause?: () => void;
  onResume?: () => void;
  onEndSession?: () => void;
  isPaused?: boolean;
  isPauseWaiting?: boolean;
  shouldGlow?: boolean; // Glow effect when conversation appears to be ending
  canEndSession?: boolean; // 🔢 Enable/disable end session button based on exchange count
  variant?: 'desktop' | 'mobile'; // Layout variant
}

export default function SessionControlButtons({
  onPause,
  onResume,
  onEndSession,
  isPaused = false,
  isPauseWaiting = false,
  shouldGlow = false,
  canEndSession = true, // Default to enabled for backwards compatibility
  variant = 'desktop'
}: SessionControlButtonsProps) {
  const { t } = useTranslation();
  
  // Desktop: centered, floating between messages
  // Mobile: bottom-fixed, always visible
  const containerClasses = variant === 'desktop'
    ? 'flex items-center justify-center gap-6 py-4'
    : 'fixed bottom-6 left-0 right-0 flex items-center justify-center gap-6 px-4 z-30';
  
  return (
    <div className={containerClasses}>
      {/* Pause/Resume Button - Yellow/Blue circular button */}
      {onPause && onResume && (
        isPaused ? (
          <button
            onClick={onResume}
            className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 active:scale-95 flex items-center justify-center group cursor-pointer"
            title={t('scenario.session.resume')}
            aria-label={t('scenario.session.resume')}
          >
            <PlayIcon className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" />
          </button>
        ) : (
          <button
            onClick={onPause}
            disabled={isPauseWaiting}
            className={`w-16 h-16 md:w-20 md:h-20 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 active:scale-95 flex items-center justify-center group ${
              isPauseWaiting 
                ? 'bg-gray-400 cursor-wait' 
                : 'bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700 cursor-pointer'
            }`}
            title={isPauseWaiting ? t('scenario.session.waitingForAvatarToStop') : t('scenario.session.pause')}
            aria-label={isPauseWaiting ? t('scenario.session.waitingForAvatarToStop') : t('scenario.session.pause')}
          >
            {isPauseWaiting ? (
              <svg className="animate-spin w-8 h-8 md:w-10 md:h-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <PauseIcon className="w-8 h-8 md:w-10 md:h-10 text-white" />
            )}
          </button>
        )
      )}
      
      {/* End Call Button - Red circular button (like phone hangup) */}
      {onEndSession && (
        <button
          onClick={onEndSession}
          disabled={!canEndSession}
          className={`w-16 h-16 md:w-20 md:h-20 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 active:scale-95 flex items-center justify-center group relative ${
            canEndSession
              ? 'bg-red-500 hover:bg-red-600 active:bg-red-700 cursor-pointer'
              : 'bg-gray-400 cursor-not-allowed opacity-50'
          } ${
            shouldGlow && canEndSession ? 'animate-pulse-glow' : ''
          }`}
          title={canEndSession ? t('scenario.session.stopAndEvaluate') : t('scenario.session.waitingForExchanges', 'Wait for at least 3 exchanges')}
          aria-label={canEndSession ? t('scenario.session.stopAndEvaluate') : t('scenario.session.waitingForExchanges', 'Wait for at least 3 exchanges')}
        >
          {/* Glow effect when conversation is ending (only if enabled) */}
          {shouldGlow && canEndSession && (
            <>
              <div className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75 pointer-events-none"></div>
              <div className="absolute inset-0 rounded-full bg-red-400 blur-xl opacity-50 animate-pulse pointer-events-none"></div>
            </>
          )}
          
          {/* Phone hangup icon - rotated to look like traditional "hang up" gesture */}
          <PhoneIcon className="w-8 h-8 md:w-10 md:h-10 text-white transform rotate-[135deg] relative z-10" />
        </button>
      )}
    </div>
  );
}

